import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import { fetchAdminFaceLivenessReviews } from '../services/api/admin';
import type { AdminFaceLivenessReview } from '../types/admin';
import { formatDateTime } from '../utils/companyOwner';
import '../styles/register.css';
import '../styles/company-owner.css';
import '../styles/admin.css';

function statusPillClass(status: string | null | undefined): string {
  const normalized = status?.toUpperCase();
  if (normalized === 'ACCEPTED' || normalized === 'CLEARED') return 'admin-pill admin-pill-success';
  if (normalized === 'LOCKED' || normalized === 'DISABLED') return 'admin-pill admin-pill-danger';
  if (normalized === 'MESSAGE_SENT') return 'admin-pill admin-pill-warning';
  return 'admin-pill';
}

const AdminFaceLivenessReviewsPage: React.FC = () => {
  const [reviews, setReviews] = useState<AdminFaceLivenessReview[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    query: '',
    status: 'PENDING',
    faceLiveness: '',
    hasFaceLivenessPhotos: 'true',
    enabled: '',
    locked: '',
  });

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetchAdminFaceLivenessReviews({
          query: filters.query.trim(),
          status: filters.status,
          ...(filters.faceLiveness ? { faceLiveness: filters.faceLiveness === 'true' } : {}),
          ...(filters.hasFaceLivenessPhotos ? { hasFaceLivenessPhotos: filters.hasFaceLivenessPhotos === 'true' } : {}),
          ...(filters.enabled ? { enabled: filters.enabled === 'true' } : {}),
          ...(filters.locked ? { locked: filters.locked === 'true' } : {}),
          page,
          size: 10,
          sort: 'updatedDate,desc',
        });

        if (!mounted) return;
        setReviews(response.items);
        setTotalPages(response.totalPages);
        setTotalElements(response.totalElements);
      } catch (loadError: any) {
        if (!mounted) return;
        setError(loadError.message || 'Could not load face liveness reviews.');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      mounted = false;
    };
  }, [filters, page]);

  const stats = useMemo(() => {
    const complete = reviews.filter((review) => review.faceLivenessComplete).length;
    const needsAttention = reviews.filter((review) =>
      ['PENDING', 'MESSAGE_SENT'].includes(String(review.status).toUpperCase()),
    ).length;
    const restricted = reviews.filter((review) =>
      ['LOCKED', 'DISABLED'].includes(String(review.status).toUpperCase()),
    ).length;
    return { complete, needsAttention, restricted };
  }, [reviews]);

  return (
    <AdminShell
      badge="Face Liveness Review"
      copy="Occasionally inspect submitted customer face photos and record a manual outcome without changing the customer app flow."
      title="Customer face review queue"
    >
      {error ? <div className="register-inline-error">{error}</div> : null}

      <div className="admin-grid admin-grid-cards" style={{ marginBottom: '18px' }}>
        <div className="admin-panel admin-stat-card">
          <span className="admin-stat-label">Queue total</span>
          <strong>{totalElements}</strong>
          <span className="admin-stat-note">Reviews matching the current filters.</span>
        </div>
        <div className="admin-panel admin-stat-card">
          <span className="admin-stat-label">Complete sets</span>
          <strong>{stats.complete}</strong>
          <span className="admin-stat-note">Users with straight, left, and right photos present.</span>
        </div>
        <div className="admin-panel admin-stat-card">
          <span className="admin-stat-label">Needs attention</span>
          <strong>{stats.needsAttention}</strong>
          <span className="admin-stat-note">Pending reviews or users already messaged for corrections.</span>
        </div>
        <div className="admin-panel admin-stat-card">
          <span className="admin-stat-label">Restricted</span>
          <strong>{stats.restricted}</strong>
          <span className="admin-stat-note">Accounts locked or disabled from this review workflow.</span>
        </div>
      </div>

      <section className="admin-panel">
        <div className="admin-panel-header">
          <div>
            <h2 className="admin-panel-title">Queue filters</h2>
            <p className="admin-panel-copy">Find customers by name, email, phone, photo completion, review state, and account state.</p>
          </div>
        </div>

        <div className="admin-toolbar admin-face-review-toolbar">
          <input
            className="register-input admin-toolbar-wide"
            onChange={(event) => {
              setPage(0);
              setFilters((current) => ({ ...current, query: event.target.value }));
            }}
            placeholder="Search by user name, email, or phone"
            value={filters.query}
          />
          <select
            className="register-input"
            onChange={(event) => {
              setPage(0);
              setFilters((current) => ({ ...current, status: event.target.value }));
            }}
            value={filters.status}
          >
            <option value="">All review states</option>
            <option value="PENDING">Pending</option>
            <option value="MESSAGE_SENT">Message sent</option>
            <option value="ACCEPTED">Accepted</option>
            <option value="CLEARED">Cleared</option>
            <option value="LOCKED">Locked</option>
            <option value="DISABLED">Disabled</option>
          </select>
          <select
            className="register-input"
            onChange={(event) => {
              setPage(0);
              setFilters((current) => ({ ...current, hasFaceLivenessPhotos: event.target.value }));
            }}
            value={filters.hasFaceLivenessPhotos}
          >
            <option value="true">Has face photos</option>
            <option value="">Photo sets and missing</option>
            <option value="false">Missing face photos</option>
          </select>
          <select
            className="register-input"
            onChange={(event) => {
              setPage(0);
              setFilters((current) => ({ ...current, faceLiveness: event.target.value }));
            }}
            value={filters.faceLiveness}
          >
            <option value="">Any liveness flag</option>
            <option value="true">Marked live</option>
            <option value="false">Not marked live</option>
          </select>
          <select
            className="register-input"
            onChange={(event) => {
              setPage(0);
              setFilters((current) => ({ ...current, enabled: event.target.value }));
            }}
            value={filters.enabled}
          >
            <option value="">Enabled or disabled</option>
            <option value="true">Enabled only</option>
            <option value="false">Disabled only</option>
          </select>
          <select
            className="register-input"
            onChange={(event) => {
              setPage(0);
              setFilters((current) => ({ ...current, locked: event.target.value }));
            }}
            value={filters.locked}
          >
            <option value="">Locked or unlocked</option>
            <option value="false">Unlocked only</option>
            <option value="true">Locked only</option>
          </select>
        </div>

        {loading ? (
          <div className="register-loader" />
        ) : (
          <>
            <div className="admin-table">
              <div className="admin-table-head admin-table-head-face-reviews">
                <span>User</span>
                <span>Verification</span>
                <span>Photos</span>
                <span>Review</span>
                <span>Account</span>
                <span>Action</span>
              </div>
              {reviews.map((review) => (
                <div className="admin-table-row admin-table-row-face-reviews" key={review.userId}>
                  <span>
                    <strong>{review.user.name || 'Unnamed user'}</strong>
                    <small>{review.user.email || 'No email supplied'}</small>
                    <small>{review.user.phone || 'No phone supplied'}</small>
                  </span>
                  <span>
                    <strong>{review.user.faceLiveness ? 'Liveness complete' : 'Liveness pending'}</strong>
                    <small>{review.user.emailVerified ? 'Email verified' : 'Email pending'}</small>
                    <small>{review.user.phoneVerified ? 'Phone verified' : 'Phone pending'}</small>
                  </span>
                  <span>
                    <strong>{review.faceLivenessComplete ? '3 of 3 photos' : 'Incomplete set'}</strong>
                    <small>Straight: {review.straightPhotoPresent ? 'Yes' : 'No'}</small>
                    <small>Left/right: {review.leftPhotoPresent ? 'Yes' : 'No'} / {review.rightPhotoPresent ? 'Yes' : 'No'}</small>
                  </span>
                  <span>
                    <span className={statusPillClass(review.status)}>{review.status || 'PENDING'}</span>
                    <small>{review.reasonCode || 'No reason recorded'}</small>
                    <small>{review.reviewedAt ? formatDateTime(review.reviewedAt) : 'Not reviewed'}</small>
                  </span>
                  <span>
                    <strong>{review.user.enabled === false ? 'Disabled' : 'Enabled'}</strong>
                    <small>{review.user.accountNonLocked === false ? 'Locked' : 'Unlocked'}</small>
                  </span>
                  <span className="admin-table-actions">
                    <Link className="register-submit register-submit-inline" to={`/admin/face-liveness-reviews/${review.userId}`}>
                      Review
                    </Link>
                  </span>
                </div>
              ))}
              {!reviews.length ? <p className="admin-empty">No users match this face review view right now.</p> : null}
            </div>

            <div className="admin-pagination">
              <button
                className="register-secondary-button"
                disabled={page === 0}
                onClick={() => setPage((current) => Math.max(current - 1, 0))}
                type="button"
              >
                Previous
              </button>
              <span>
                Page {page + 1} of {Math.max(totalPages, 1)}
              </span>
              <button
                className="register-secondary-button"
                disabled={page + 1 >= Math.max(totalPages, 1)}
                onClick={() => setPage((current) => current + 1)}
                type="button"
              >
                Next
              </button>
            </div>
          </>
        )}
      </section>
    </AdminShell>
  );
};

export default AdminFaceLivenessReviewsPage;
